/** Retail catalog uids returned by GET /products/manual. Falls back to the signed-in user. */
export function manualProductCatalogUids(catalogUids, fallbackUid) {
  const ids = [
    ...new Set(
      (Array.isArray(catalogUids) ? catalogUids : [])
        .map((id) => String(id || "").trim())
        .filter(Boolean)
    ),
  ];
  if (ids.length) return ids;
  const fallback = String(fallbackUid || "").trim();
  return fallback ? [fallback] : [];
}

/** Typesense filter. One uid stays an equality; several uids match any of them. */
export function manualProductUidFilter(catalogUids, fallbackUid) {
  const ids = manualProductCatalogUids(catalogUids, fallbackUid);
  if (!ids.length) return "";
  if (ids.length === 1) return `uid:=${ids[0]}`;
  return `uid:=[${ids.join(",")}]`;
}

/**
 * Same split as Orders: admins can switch to seller products, and that list
 * does not include the admin catalog. Pro accounts stay on their own products.
 */
export function showsSellerCatalogToggle(user = {}) {
  return Boolean(user?.isEnterpriseAdmin) && !user?.isProUser;
}

/**
 * `catalog` is the admin (or seller) catalog. `sellers` is every other uid
 * in the enterprise, so the two lists never mix.
 */
export function manualProductSearchUidFilter({
  scope = "catalog",
  catalogUids,
  uid,
} = {}) {
  const ids = manualProductCatalogUids(catalogUids, uid);
  if (scope === "sellers") {
    if (!ids.length) return "";
    return ids.map((id) => `uid:!=${id}`).join(" && ");
  }
  return manualProductUidFilter(catalogUids, uid);
}

/** Blank the customizer needs. Search documents often only have `blankProductId`. */
export function blankIdFromProduct(product) {
  return (
    product?.productMapping?.blankProductIds?.[0] ||
    product?.blankProductIds?.[0] ||
    product?.blankProductId ||
    null
  );
}

/**
 * Typesense hits omit the blank and linked stores. Skip the detail read when
 * the hit or the loaded row already has a blank. The loaded blank still has
 * to be copied onto the hit; search documents do not include it.
 */
export function searchHitNeedsProductDetails(hit, live) {
  if (!hit?.id || isStoreImportProduct(hit)) return false;
  return !blankIdFromProduct(live) && !blankIdFromProduct(hit);
}

/** Search row with the loaded product's blank and stores, then any fresh read. */
export function searchRowFromHit(hit, live, fetched) {
  let row = hit;
  if (live) row = applyProductDetailsToSearchHit(row, live);
  if (fetched) row = applyProductDetailsToSearchHit(row, fetched);
  return row;
}

/** Copy the fields the products row needs off a full manual product. */
export function applyProductDetailsToSearchHit(hit, detail) {
  if (!detail || typeof detail !== "object") return hit;
  const blankProductId = blankIdFromProduct(detail);
  return {
    ...hit,
    ...(detail.productMapping ? { productMapping: detail.productMapping } : {}),
    ...(Array.isArray(detail.linkedShops) ? { linkedShops: detail.linkedShops } : {}),
    ...(Array.isArray(detail.linkedListings)
      ? { linkedListings: detail.linkedListings }
      : {}),
    ...(Array.isArray(detail.activeShops) ? { activeShops: detail.activeShops } : {}),
    ...(detail.channels && typeof detail.channels === "object"
      ? { channels: detail.channels }
      : {}),
    ...(blankProductId ? { blankProductId } : {}),
  };
}

/** Import sidecar ids are `migm` + 16 hex. Listing ids are `mig` + 16 hex. */
const IMPORTED_MANUAL_PRODUCT_ID = /^migm[0-9a-f]{16}$/;

/** Shop ids this product is published to or linked from. */
export function productChannelShopIds(product = {}) {
  const ids = [];
  const seen = new Set();
  const add = (id) => {
    const key = String(id || "").trim();
    if (!key || seen.has(key)) return;
    seen.add(key);
    ids.push(key);
  };
  (product.activeShops || []).forEach(add);
  (product.linkedShops || []).forEach(add);
  const channels = product.channels;
  if (channels && typeof channels === "object" && !Array.isArray(channels)) {
    Object.keys(channels).forEach(add);
  }
  const status = product.channelStatus;
  if (status && typeof status === "object" && !Array.isArray(status)) {
    Object.keys(status).forEach(add);
  }
  return ids;
}

export function productIsInChannel(product) {
  return productChannelShopIds(product).length > 0;
}

export function productMatchesChannel(product, shopId) {
  const id = String(shopId || "").trim();
  if (!id) return true;
  return productChannelShopIds(product).includes(id);
}

/** Unsynced store imports belong on Listings, not in the retail catalog. */
export function isStoreImportProduct(product) {
  if (!product) return false;
  if (product.importedFromWebsite) return true;
  return IMPORTED_MANUAL_PRODUCT_ID.test(String(product.id || ""));
}

export function manualProductVisible(product, catalogUids, fallbackUid) {
  if (!product?.uid) return true;
  return manualProductCatalogUids(catalogUids, fallbackUid).includes(
    String(product.uid)
  );
}
